import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
    Stack, Chip, IconButton, Typography, useMediaQuery, Alert, Grid, Box, Autocomplete,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PaidIcon from "@mui/icons-material/Paid";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import SaveIcon from "@mui/icons-material/Save";
import Api from "../../customlib/Api";
import Global from "../../customlib/Global";
import livechatApi from "./livechatApi";
import {
    LEAD_TYPES, GENDERS, OCCASIONS, REASON_GROUPS, QUICK_LABELS, MAIN_LABELS,
    MAIN_LABEL_FIELDS, RANDOM_LABEL,
} from "./livechatConstants";
import { PASTEL } from "./livechatTheme";
import { Shuffle } from "@mui/icons-material";

const emptyForm = {
    leadType: "", productCategory: "", deliveryDate: "", city: "", gender: "",
    occasion: "", reasonGroup: "", reason: "",
};

const MAIN_LABEL_CHIP_STYLES = {
    pending: { fontWeight: 600, backgroundColor: PASTEL.primarySoft, color: PASTEL.primaryDark },
    cancel: { fontWeight: 600, backgroundColor: PASTEL.primarySoft, color: PASTEL.primaryDark },
    paid: { fontWeight: 600, backgroundColor: "#e6f4ea", color: "#1e7e34" },
    unpaid: { fontWeight: 600, backgroundColor: "#e3f2fd", color: "#0d5eaf" },
};

// FR-A-16 — replikasi form "Tentukan Label Pelanggan" Lotus. Dropdown desktop / bottom-sheet
// mobile (fullScreen di breakpoint kecil), sesuai mockup di DEV-1049.
export default function SetLabelDialog({ open, session, onClose, onSaved }) {
    const fullScreen = useMediaQuery("(max-width:600px)");
    const [selectedLabel, setSelectedLabel] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [cityList, setCityList] = useState([]);
    const [loadingCities, setLoadingCities] = useState(false);

    const productCategories = useMemo(() => {
        try {
            return Global.getListProductCategories() || [];
        } catch {
            return [];
        }
    }, []);

    useEffect(() => {
        if (!open || cityList.length > 0 || loadingCities) return;
        setLoadingCities(true);
        axios
            .post(Api.getCityList(), {}, Api.getRequestHeader())
            .then((response) => {
                setCityList(response.data.data || []);
            })
            .catch((e) => {
                console.error(
                    "Gagal memuat daftar kota",
                    e?.response?.status, e?.response?.data || e.message
                );
            })
            .finally(() => setLoadingCities(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (open) {
            setSelectedLabel(session?.label?.labelType || null);
            setForm({
                leadType: session?.label?.leadType || "",
                productCategory: session?.label?.productCategory || "",
                deliveryDate: session?.label?.deliveryDate ? session.label.deliveryDate.slice(0, 16) : "",
                city: session?.label?.city || "",
                gender: session?.label?.gender || "",
                occasion: session?.label?.occasion || "",
                reasonGroup: session?.label?.reasonGroup || "",
                reason: session?.label?.reason || "",
                explanation: session?.label?.explanation || "",
                issue: session?.label?.issue || "",
                commonReason: session?.label?.commonReason || "",
                detailedReason: session?.label?.detailedReason || "",
            });
            setError("");
        }
    }, [open, session]);

    const isMainLabel = selectedLabel && MAIN_LABELS.includes(selectedLabel);
    const isRandomLabel = selectedLabel && RANDOM_LABEL === selectedLabel;
    const isQuickLabel = selectedLabel && !isMainLabel && !isRandomLabel;

    const mainLabelFields = MAIN_LABEL_FIELDS[selectedLabel] || [];

    // Paid/Unpaid adalah tindak lanjut dari label Pending — hanya boleh dipilih kalau
    // sesi saat ini memang sudah berlabel "pending" (bukan sekadar dipilih di form yang
    // belum disimpan), supaya tidak mengganggu alur pelabelan lain.
    const canSetPaidUnpaid = session?.label?.labelType === "pending";

    const requiredMissing = () =>
        mainLabelFields.filter((key) => !form[key]);

    const handleClearSelection = () => {
        // Sengaja tidak reset `form` di sini — nilai yang sudah diisi user harus tetap
        // terbawa saat berpindah-pindah antar label (pending/cancel/paid/unpaid/random/quick).
        setSelectedLabel(null);
        setError("");
    };

    const handleSave = async () => {
        setError("");

        if (isMainLabel) {
            const missing = requiredMissing();
            if (missing.length > 0) {
                setError(`Field wajib belum diisi: ${missing.join(", ")}`);
                return;
            }
        }

        setSaving(true);
        try {
            const body = isMainLabel || isRandomLabel
                ? { labelType: selectedLabel, ...form }
                : { labelType: selectedLabel };
            const { data } = await livechatApi.setLabel(session._id, body);
            onSaved && onSaved(data.data);
            onClose();
        } catch (e) {
            setError(e?.response?.data?.message || "Gagal menyimpan label");
        } finally {
            setSaving(false);
        }
    };

    const reasonOptions = form.reasonGroup ? (REASON_GROUPS[form.reasonGroup] || []) : [];

    return (
        <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <LabelOutlinedIcon sx={{ color: PASTEL.primary }} fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={700}>Tentukan Label Percakapan</Typography>
                </Stack>
                <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ backgroundColor: PASTEL.background }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

                {selectedLabel && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <Chip
                            label={selectedLabel}
                            onDelete={handleClearSelection}
                            sx={MAIN_LABEL_CHIP_STYLES[selectedLabel] || { fontWeight: 600 }}
                        />
                        <Typography variant="caption" color="text.secondary">Label terpilih</Typography>
                    </Stack>
                )}

                {!selectedLabel && (
                    <Stack spacing={2.5}>
                        <Box>
                            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                                Label Utama
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mt: 0.5 }}>
                                <Button
                                    variant="outlined" color="warning"
                                    startIcon={<ScheduleIcon />}
                                    onClick={() => setSelectedLabel("pending")}
                                    sx={{ textTransform: "none", py: 1, flex: "1 1 30%", minWidth: 140 }}
                                >
                                    Pending
                                </Button>
                                <Button
                                    variant="outlined" color="error"
                                    startIcon={<CancelOutlinedIcon />}
                                    onClick={() => setSelectedLabel("cancel")}
                                    sx={{ textTransform: "none", py: 1, flex: "1 1 30%", minWidth: 140 }}
                                >
                                    Cancel
                                </Button>
                                {canSetPaidUnpaid && (
                                    <Button
                                        variant="outlined" color="success"
                                        startIcon={<PaidIcon />}
                                        onClick={() => setSelectedLabel("paid")}
                                        sx={{ textTransform: "none", py: 1, flex: "1 1 30%", minWidth: 140 }}
                                    >
                                        Paid
                                    </Button>
                                )}
                                {canSetPaidUnpaid && (
                                    <Button
                                        variant="outlined" color="info"
                                        startIcon={<MoneyOffIcon />}
                                        onClick={() => setSelectedLabel("unpaid")}
                                        sx={{ textTransform: "none", py: 1, flex: "1 1 30%", minWidth: 140 }}
                                    >
                                        Unpaid
                                    </Button>
                                )}
                                <Button
                                    variant="outlined" color="secondary"
                                    startIcon={<Shuffle />}
                                    onClick={() => setSelectedLabel(RANDOM_LABEL)}
                                    sx={{ textTransform: "capitalize", py: 1, flex: "1 1 30%", minWidth: 140 }}
                                >
                                    {RANDOM_LABEL}
                                </Button>
                            </Stack>
                        </Box>

                        <Box>
                            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                                Label Cepat Khusus CS
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                                {QUICK_LABELS.map((q) => (
                                    <Chip
                                        key={q.label}
                                        label={q.label}
                                        onClick={() => setSelectedLabel(q.label)}
                                        sx={{ backgroundColor: q.color, color: "#fff", fontWeight: 500, "&:hover": { opacity: 0.85 } }}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    </Stack>
                )}

                {isMainLabel && (
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        {mainLabelFields.includes("leadType") && (
                            <Grid item xs={12} sm={6}>
                                <TextField select fullWidth size="small" label="Tipe Leads" value={form.leadType}
                                    onChange={(e) => setForm({ ...form, leadType: e.target.value })}>
                                    {LEAD_TYPES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                                </TextField>
                            </Grid>
                        )}

                        {mainLabelFields.includes("productCategory") && (
                            <Grid item xs={12} sm={6}>
                                <TextField select fullWidth size="small" label="Kategori Produk" value={form.productCategory}
                                    onChange={(e) => setForm({ ...form, productCategory: e.target.value })}>
                                    {productCategories.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                                </TextField>
                            </Grid>
                        )}

                        {mainLabelFields.includes("deliveryDate") && (
                            <Grid item xs={12} sm={6}>
                                <TextField type="datetime-local" fullWidth size="small" label="Tanggal Pengiriman" InputLabelProps={{ shrink: true }}
                                    value={form.deliveryDate} onChange={(e) => setForm({ ...form, deliveryDate: e.target.value })} />
                            </Grid>
                        )}

                        {mainLabelFields.includes("city") && (
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    freeSolo
                                    fullWidth
                                    size="small"
                                    loading={loadingCities}
                                    loadingText="Memuat data kota..."
                                    options={cityList.map((c) => String(c.name))}
                                    value={form.city}
                                    onChange={(_event, newValue) => setForm({ ...form, city: newValue || "" })}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Kota"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingCities ? <CircularProgress color="inherit" size={16} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        {mainLabelFields.includes("gender") && (
                            <Grid item xs={12} sm={6}>
                                <TextField select fullWidth size="small" label="Gender" value={form.gender}
                                    onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                                    {GENDERS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                                </TextField>
                            </Grid>
                        )}

                        {mainLabelFields.includes("occasion") && (
                            <Grid item xs={12} sm={6}>
                                <TextField select fullWidth size="small" label="Occasion" value={form.occasion}
                                    onChange={(e) => setForm({ ...form, occasion: e.target.value })}>
                                    {OCCASIONS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                                </TextField>
                            </Grid>
                        )}

                        {mainLabelFields.includes("reasonGroup") && (
                            <Grid item xs={12} sm={6}>
                                <TextField select fullWidth size="small" label="Group Alasan" value={form.reasonGroup}
                                    onChange={(e) => setForm({ ...form, reasonGroup: e.target.value, reason: "" })}>
                                    {Object.keys(REASON_GROUPS).map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                                </TextField>
                            </Grid>
                        )}

                        {mainLabelFields.includes("reason") && (
                            <Grid item xs={12} sm={6}>
                                <TextField select fullWidth size="small" label="Alasan" value={form.reason} disabled={!form.reasonGroup}
                                    onChange={(e) => setForm({ ...form, reason: e.target.value })}>
                                    {reasonOptions.length > 0
                                        ? reasonOptions.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)
                                        : <MenuItem value="" disabled>Tidak ada pilihan</MenuItem>}
                                </TextField>
                            </Grid>
                        )}
                    </Grid>
                )}

                {isRandomLabel && (
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={12}>
                            <TextField fullWidth size="small" label="Keterangan" value={form.explanation}
                                onChange={(e) => setForm({ ...form, explanation: e.target.value })}>
                                {form.explanation}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={12}>
                            <TextField select fullWidth size="small" label="Permasalahan" value={form.issue}
                                onChange={(e) => setForm({ ...form, issue: e.target.value, commonReason: "", detailedReason: "" })}>
                                {Global.treePermasalan.map((v, index) => <MenuItem key={`issue-${index}`} value={v.permasalahan}>{v.permasalahan}</MenuItem>)}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={12}>
                            <TextField select fullWidth size="small" label="Alasan Umum" value={form.commonReason} disabled={!form.issue}
                                onChange={(e) => setForm({ ...form, commonReason: e.target.value, detailedReason: "" })}>
                                {(Global.treePermasalan.find((v) => v.permasalahan === form.issue)?.alasanUmum || []).map((v, index) => <MenuItem key={`common-reason-${index}`} value={v.name}>{v.name}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <TextField select fullWidth size="small" label="Alasan Rinci" value={form.detailedReason} disabled={!form.commonReason}
                                onChange={(e) => setForm({ ...form, detailedReason: e.target.value })}>
                                {(Global.treePermasalan.find((v) => v.permasalahan === form.issue)?.alasanUmum || []).find((v) => v.name === form.commonReason)?.alasanRinci.map((v, index) => <MenuItem key={`detailed-reason-${index}`} value={v}>{v}</MenuItem>)}
                            </TextField>
                        </Grid>
                    </Grid>
                )}

                {isQuickLabel && (
                    <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                        Label cepat tidak butuh field pendukung — klik Simpan untuk menyimpan langsung.
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} sx={{ textTransform: "none" }}>Batal</Button>
                <Button
                    variant="contained"
                    startIcon={<SaveIcon fontSize="small" />}
                    disabled={!selectedLabel || saving || (isMainLabel && requiredMissing().length > 0)}
                    onClick={handleSave}
                    sx={{
                        textTransform: "none",
                        backgroundColor: PASTEL.primary,
                        "&:hover": { backgroundColor: PASTEL.primaryDark },
                    }}
                >
                    {saving ? "Menyimpan..." : "Simpan"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
